import {
  childPath,
  adultPath,
  elderPath,
  deathPath
} from "./illustrationPathStrings";
import colorFn from "./colorFn";

const fillGivenStatus = (status, immuno) => {
  if (status === "SUSCEPTIBLE") {
    return immuno ? colorFn("IMMUNO") : colorFn("SUSCEPTIBLE");
  }

  /*
  if (status === "RECOVERED") {
    return immuno ? colorFn("IMMUNO") : colorFn("RECOVERED");
  }
  */

  if (status === "SEVERE_CASE" || status === "HOSPITALIZED") {
    return "white";
  }

  return colorFn(status);
};

const initializeSimulationState = graph => {
  const generateFreshPopulation = () => {
    const nodeStatusHash = {};
    graph.nodes.forEach(n => {
      nodeStatusHash[n.id] = {
        status: "SUSCEPTIBLE",
        infectionDate: null
      };
    });
    nodeStatusHash["a"].status = "INFECTED";
    return nodeStatusHash;
  };

  return {
    population: generateFreshPopulation(),
    queue: [],
    history: [
      {
        SUSCEPTIBLE: graph.nodes.length - 1,
        RECOVERED: 0,
        SEVERE_CASE: 0,
        HOSPITALIZED: 0,
        DIED: 0,
        INFECTED: 1
      }
    ]
  };
};

const removeFromHospitalQueue = (queue, id) => {
  const index = queue.indexOf(id);
  if (index >= 0) {
    queue.splice(index, 1);
  }
};

const defaultLinksInPlayFn = (type, day) => {
  return true;
};

const simulator = (
  nodes,
  neighboursLookup,
  previousSimState,
  {
    numberOfBeds = 1,
    linksInPlay = defaultLinksInPlayFn,
    /*
    isEWorkOn = true,
    isNEWorkOn = true,
    isFriendsOn = true,
    isFamilyOn = true,
    isIFamilyOn = true,
    */
    infectionChance = 0.02,
    infectionLength = 22,
    percentageNotSevere = 0.9,
    immunoNoBeds,
    immunoWithBed,
    severeNoBeds,
    severeWithBeds
  }
) => {
  const { population, history, queue } = previousSimState;

  const day = history.length;

  /*
  Math.pow(x, l) = c
  Math.log(Math.pow(x, l)) = Math.log(c)
  l * Math.log(x) = Math.log(c)
  Math.log(x) = Math.log(c) * (1 / l)
  Math.log(x) = Math.log(Math.pow(c, 1 / l))
  x = Math.pow(c, 1 / l)
  */

  const dailySurvivalRateGivenInfectionLength = c => {
    return Math.pow(c, 1 / infectionLength);
  };

  const statusGivenBedAvailability = (queue, id) => {
    if (queue.indexOf(id) < 0) {
      queue.push(id);
    }

    if (queue.indexOf(id) < numberOfBeds) {
      return "HOSPITALIZED";
    } else {
      return "SEVERE_CASE";
    }
  };

  const INFECTED_TO_SEVERE_RATE = dailySurvivalRateGivenInfectionLength(
    percentageNotSevere
  );

  const HEALTHY_HOSPITALIZED = dailySurvivalRateGivenInfectionLength(
    severeWithBeds
  );
  const HEALTHY_NO_BEDS = dailySurvivalRateGivenInfectionLength(severeNoBeds);
  const IMMUNOC_HOSPITALIZED = dailySurvivalRateGivenInfectionLength(
    immunoWithBed
  );
  const IMMUNOC_NO_BEDS = dailySurvivalRateGivenInfectionLength(immunoNoBeds);

  const tallies = {
    SUSCEPTIBLE: 0,
    INFECTED: 0,
    SEVERE_CASE: 0,
    HOSPITALIZED: 0,
    DIED: 0,
    RECOVERED: 0
  };

  let statusChanges = {
    INFECTED: [],
    SEVERE_CASE: [],
    HOSPITALIZED: [],
    DIED: [],
    RECOVERED: []
  };

  /*
  const inPlayHash = {
    "IMMEDIATE-FAMILY": isIFamilyOn,
    FAMILY: isFamilyOn,
    "ESSENTIAL-WORK": isEWorkOn,
    "NON-ESSENTIAL-WORK": isNEWorkOn,
    FRIENDS: isFriendsOn
  };
  */

  nodes.forEach(n => {
    let nextStatus = population[n.id].status;

    if (population[n.id].status === "SUSCEPTIBLE") {
      const neighbours = neighboursLookup[n.id].neighbours;
      const infectedNeighbours = neighbours.filter(d => {
        const inPlay = linksInPlay(d.priority, day); // inPlayHash[d.priority];

        return (
          inPlay &&
          (population[d.id].status === "INFECTED" ||
            population[d.id].status === "SEVERE_CASE")
        );
      });

      const totalInfectionChance = Math.pow(
        1 - infectionChance,
        infectedNeighbours.length
      );
      const infectionEvent = Math.random() > totalInfectionChance;

      if (infectionEvent) {
        if (n.immuno) {
          nextStatus = statusGivenBedAvailability(queue, n.id);
        } else {
          nextStatus = "INFECTED";
        }
      }
    }

    if (population[n.id].status === "INFECTED") {
      if (day - population[n.id].infectionDate > infectionLength) {
        // 10 days without increase in severity ==> recovers
        nextStatus = "RECOVERED";
      } else {
        if (Math.random() > INFECTED_TO_SEVERE_RATE) {
          // Gets bad
          nextStatus = statusGivenBedAvailability(queue, n.id);
        }
      }
    }

    if (population[n.id].status === "HOSPITALIZED") {
      const livesForNow = n.immuno
        ? IMMUNOC_HOSPITALIZED
        : HEALTHY_HOSPITALIZED;

      if (Math.random() > livesForNow) {
        nextStatus = "DIED";
        removeFromHospitalQueue(queue, n.id);
      } else {
        if (day - population[n.id].infectionDate > infectionLength) {
          nextStatus = "RECOVERED";
          removeFromHospitalQueue(queue, n.id);
        }
      }
    }

    if (population[n.id].status === "SEVERE_CASE") {
      const livesForNow = n.immuno ? IMMUNOC_NO_BEDS : HEALTHY_NO_BEDS;

      if (Math.random() > livesForNow) {
        nextStatus = "DIED";
        removeFromHospitalQueue(queue, n.id);
      } else {
        if (day - population[n.id].infectionDate > infectionLength) {
          nextStatus = "RECOVERED";
          removeFromHospitalQueue(queue, n.id);
        } else {
          nextStatus = statusGivenBedAvailability(queue, n.id);
        }
      }
    }

    if (population[n.id].status === "RECOVERED") {
      nextStatus = "RECOVERED"; // tallies["RECOVERED"] = tallies["RECOVERED"] ? tallies["RECOVERED"] + 1 : 1;
    }

    tallies[nextStatus] = tallies[nextStatus] ? tallies[nextStatus] + 1 : 1;

    if (population[n.id].status !== nextStatus) {
      statusChanges[nextStatus].push(n.id);
    }
  });

  // Write to State
  let nextPopulation = { ...population };

  Object.keys(statusChanges).forEach(statusKey => {
    statusChanges[statusKey].forEach(nid => {
      nextPopulation[nid].status = statusKey;

      if (statusKey === "INFECTED" || statusKey === "SEVERE_CASE") {
        nextPopulation[nid].infectionDate = day;
      }
    });
  });

  let nextHistory = history.slice(0);
  nextHistory.push(tallies);

  return {
    population: nextPopulation,
    queue,
    history: nextHistory
  };
};

const linkRenderer = (
  context,
  links,
  { linksInPlay = defaultLinksInPlayFn },
  day
) => {
  context.lineWidth = 1;

  function drawLink(d) {
    context.moveTo(d.source.x, d.source.y);
    context.lineTo(d.target.x, d.target.y);
  }

  // Draw Links
  context.beginPath();
  links.forEach(d => {
    if (linksInPlay(d.priority, day)) {
      drawLink(d);
    }
  });

  context.strokeStyle = "#ddd";
  context.stroke();
};

const nodesRenderer = (
  context,
  nodes,
  nodeStates,
  day,
  frame,
  framesPerDay,
  controls
) => {
  let animationDay = day - 1;

  let drawInFront = [];

  let draw = d => {
    let jump = 0;

    if (animationDay === nodeStates[d.id].infectionDate) {
      // If this person was infected today
      jump =
        3 *
        Math.sin(((frame % framesPerDay) / (2 * framesPerDay)) * 2 * Math.PI);
      // Animate vertically to highlight the person
      // ... or maybe make them shake instead?
    }

    context.translate(d.x, d.y - jump);

    // Bubble
    if (nodeStates[d.id].status === "HOSPITALIZED") {
      context.beginPath();
      context.arc(0, 3, 12, 0, Math.PI * 2, true);
      context.lineWidth = 3;
      context.strokeStyle = colorFn("HOSPITALIZED");
      context.stroke();
      context.fillStyle = colorFn("HOSPITALIZED");
      context.fill();
    }

    if (nodeStates[d.id].status === "SEVERE_CASE") {
      context.beginPath();
      context.arc(0, 3, 12, 0, Math.PI * 2, true);
      context.lineWidth = 3;
      context.strokeStyle = colorFn("SEVERE_CASE");
      context.stroke();
      context.fillStyle = colorFn("SEVERE_CASE");
      context.fill();
    }

    let path;
    if (nodeStates[d.id].status === "DIED") {
      path = deathPath;
    } else {
      switch (d.age) {
        case "elder":
          path = elderPath;
          break;

        case "child":
          path = childPath;
          break;

        default:
          path = adultPath;
      }
    }

    let p = new Path2D(path);

    context.fillStyle =
      nodeStates[d.id].status !== "DIED"
        ? fillGivenStatus(nodeStates[d.id].status, d.immuno)
        : "black"; //(nodeStates[d.id].status === "INFECTED") ? "#c00" : ((d.immuno) ? "#a80" : "black");
    context.fill(p);
    context.translate(-d.x, -d.y + jump);
  };

  nodes.forEach(d => {
    if (
      nodeStates[d.id].status === "HOSPITALIZED" ||
      nodeStates[d.id].status === "SEVERE_CASE"
    ) {
      drawInFront.push(d);
    } else {
      draw(d);
    }
  });

  drawInFront.forEach(draw);
};

const statesList = [
  { key: "RECOVERED", label: "Recovered" },
  { key: "SUSCEPTIBLE", label: "Susceptible" },
  { key: "INFECTED", label: "Infected" },
  { key: "SEVERE_CASE", label: "Needs Hospital Bed" },
  { key: "HOSPITALIZED", label: "Hospitalized" },
  { key: "DIED", label: "Died" }
];

export {
  simulator,
  initializeSimulationState,
  statesList,
  linkRenderer,
  nodesRenderer
};
